import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import env from '../env.json'

export const isIsland =
  typeof navigator !== 'undefined' && (navigator.userAgentData?.brands?.some((b) => b.brand === 'Island') ?? false)

// coastal-blue-50/60 from @quickmd-llc/design-system — matches the primary Button color and hover
const COASTAL_BLUE_50 = '#178199'
const COASTAL_BLUE_60 = '#166374'

function getTracesSampleRate(environment: string): number {
  switch (environment) {
    case 'DEV':
      return 0
    case 'QA':
      return 0.5
    case 'UAT':
      return 0.25
    case 'PreProd':
      return 1.0
    case 'Autotest':
      return 1.0
    case 'Production':
      return 0.25
    default:
      return 0
  }
}

interface CustomErrorProps {
  // `| undefined` explicitly: `exactOptionalPropertyTypes` is on, so without it a caller
  // cannot pass a value it computed as possibly-absent without building the object
  // conditionally.
  error?: Error | undefined
  message?: string | undefined
  name: string
  status: number
}
/**
 * A dev server serving the app off the developer's own machine. Suppressed because the
 * errors it produces are unfinished local work, not anything a user hit — and because
 * the volume of them would drown the real signal.
 *
 * Deliberately NOT keyed on the deployment hostname: every deployed environment reports,
 * which is what makes the per-environment sample rates above real configuration rather
 * than dead branches, and what lets an error found on a pre-production environment be
 * triaged before it reaches a clinician.
 */
const isLocalDevServer = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

export function initSentry() {
  Sentry.init({
    enabled: !isLocalDevServer,
    dsn: 'https://f9d1dfc854f5604ee2905b64eb325d6a@o4508456417427456.ingest.us.sentry.io/4509137206444032',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
      Sentry.feedbackIntegration({
        enableScreenshot: false,
        autoInject: false,
        colorScheme: 'system',
        showBranding: false,
        useSentryUser: { email: 'email', name: 'username' },
        showEmail: true,
        showName: true,
        triggerLabel: 'Report an Issue',
        submitButtonLabel: 'Submit',
        themeLight: {
          accentBackground: COASTAL_BLUE_50,
          accentForeground: '#ffffff',
        },
        themeDark: {
          accentBackground: COASTAL_BLUE_50,
          accentForeground: '#ffffff',
        },
      }),
    ],
    tracesSampleRate: getTracesSampleRate(env.name),
    propagateTraceparent: true,
    tracePropagationTargets: [env.baseURL],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    environment: env.name,
  })

  injectSentryTriggerStyles()
  startReplayBuffer()
}

function injectSentryTriggerStyles() {
  const style = document.createElement('style')
  // --actor-background is not in Sentry's theme API — inject it via CSS custom property
  // inheritance, which pierces shadow DOM boundaries from the host element.
  style.textContent = `#sentry-feedback { --actor-background: ${COASTAL_BLUE_50}; --actor-hover-background: ${COASTAL_BLUE_60}; --actor-color: #ffffff; }`
  document.head.appendChild(style)
}

function startReplayBuffer() {
  const client = Sentry.getClient()
  const replay = client?.getIntegrationByName<{ name: string; startBuffering?: () => void }>('Replay')
  replay?.startBuffering?.()
}

export function sendCustomSentryError(e: CustomErrorProps) {
  Sentry.withScope((scope) => {
    const error = e.error || new Error(e.message || 'An unknown error occurred')
    error.name = e.name
    scope.setContext('response', {
      status: e?.status,
    })
    Sentry.captureException(error)
  })
}

export function sendCustomSentryMessage(message: string) {
  Sentry.captureMessage(message)
}

/**
 * Builds the `onError` for a `SilentErrorBoundary`. That boundary renders `null` on
 * catch, so without an `onError` the crash it swallowed reaches nothing but
 * `console.error` — and `main.tsx` stubs every `console` method out on the production
 * deployment, so on the one environment that matters it reaches nothing at all.
 *
 * Call this at module scope and pass the result as the prop, so the handler identity is
 * stable across renders.
 */
export function makeBoundaryErrorReporter(boundaryName: string) {
  return (error: Error, errorInfo: React.ErrorInfo) => {
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', boundaryName)
      scope.setContext('react', { componentStack: errorInfo.componentStack })
      Sentry.captureException(error)
    })
  }
}

/**
 * Records a Sentry breadcrumb. Breadcrumbs are attached to any error/replay
 * captured later, so they leave a trail even for freezes/hangs that never throw
 * (e.g. a tab going unresponsive after opening several external tabs at once).
 */
export function addSentryBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb)
}
